/**
 * Modal de confirmación para eliminar compensación — Sprint 17 Fase 2.
 *
 * Solo se llega aquí si la compensación NO tiene aplicaciones. La card
 * ya esconde el botón "Eliminar" cuando hay historia. Aún así, el
 * backend valida y devuelve 422 si hay aplicaciones, por si la UI llega
 * a quedar desincronizada.
 */
import { useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { eliminarCompensacion } from '../../lib/api/compensaciones';
import type { CompensacionConDetalle } from '../../lib/api/types';
import { Button } from '../ui/Button';

interface Props {
  compensacion: CompensacionConDetalle;
  onCerrar: () => void;
  onEliminada: () => void;
}

export function ModalConfirmarEliminar({
  compensacion,
  onCerrar,
  onEliminada,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirmar() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await eliminarCompensacion(compensacion.id);
      onEliminada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Eliminar compensación"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <h2 className="text-lg font-medium text-text-primary">
          Eliminar compensación
        </h2>

        <p className="text-sm text-text-secondary">
          ¿Eliminar la compensación{' '}
          <strong className="text-text-primary">{compensacion.descripcion}</strong>{' '}
          (Casa {compensacion.lote_identificador} · {compensacion.vecino_nombre})?
        </p>

        <div className="bg-state-danger/[0.08] border-l-4 border-state-danger p-3 rounded-sm text-xs text-text-secondary">
          Esta acción es irreversible. Solo se permite porque la compensación{' '}
          <strong>no tiene aplicaciones registradas</strong>. Si fue un error
          de registro, esta opción te lo permite limpiar.
        </div>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirmar}
            disabled={submitting}
          >
            {submitting ? 'Eliminando…' : 'Sí, eliminar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
